<template>
  <div
    v-for="field in fields"
    :key="field.id"
    class="revision-field panel"
  >
    <div
      v-if="field.label"
      class="revision-field-label"
    >
      <div class="field-label">
        <span class="label-text">{{ field.label }}</span>
      </div>
    </div>
    <div
      v-for="(section, sectionIndex) in field.sections"
      :key="section.id"
      class="revision-section"
      :class="{ 'mb-3': !(sectionIndex === field.sections.length - 1) }"
    >
      <div
        v-for="block in section.blocks"
        :key="block.id"
        :class="['revision-block', copyBlock === block.id && !recentCopy ? 'copy-block' : '']"
      >
        <v-menu
          :offset="[-40, 0]"
          open-on-hover
          :close-delay="500"
          :open-delay="500"
          :min-width="recentCopy === block.id ? '115px' : 'auto'"
          z-index="2"
          location="right"
          content-class="revision-menu-content"
          transition="fade-transition"
        >
          <template #activator="{ props }">
            <div
              class="revision-field-html"
              v-bind="props"
            >
              <DpplSafeHtml :html="block.html" />
            </div>
          </template>

          <v-sheet
            class="copy-menu pa-3 cursor-pointer"
            rounded="lg"
            @mouseover="copyBlock = block.id"
            @mouseleave="copyBlock = null"
            @click.stop="onCopyClick(block.html, block.id)"
          >
            <p v-if="recentCopy === block.id">
              Text Copied!
            </p>
            <v-icon
              v-else
              icon="$content-copy"
            />
          </v-sheet>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import { useEditor } from '@/stores/editor/editor';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';

export default {
  name: 'RevisionContent',
  components: { DpplSafeHtml },

  props: {
    fields: { type: Object, required: true },
  },

  emits: ['copyClicked'],

  data() {
    return {
      copyBlock: null,
      recentCopy: null,
    };
  },

  methods: {
    ...mapActions(useEditor, ['copyToClipboard']),

    onCopyClick(html, id) {
      this.$emit('copyClicked');
      this.copyToClipboard({ content: html, isHtml: true });
      this.recentCopy = id;
      this.copyBlock = null;
      setTimeout(() => {
        this.recentCopy = null;
      }, 1500);
    },
  },
};
</script>

<style scoped lang="less">
.revision-field {
  width: 100%;
}
.revision-field-label {
  width: 100%;
  position: relative;

  :deep(.field-label) {
    width: 100%;
    position: absolute;
    left: 0;
    top: -36px;
    border-bottom: 1px solid @stroke-default;
    user-select: none;
    height: 20px;
    color: @text-darkest;
    font-size: 10px;
    font-weight: 400;
    .label-text {
      position: absolute;
      right: 0;
      text-align: right;
    }
  }
}
.panel {
  padding: 16px 16px 16px 0;
  width: 100%;
  margin-bottom: 28px;
  border-radius: 10px;
}
.revision-block {
  position: relative;
  word-break: break-word;
}
.revision-field-html {
  text-align: left;

  & {
    :deep(h1), :deep(h2), :deep(h3) {
      line-height: 28px;
      font-weight: 700;
      padding: 8px 40px 8px 0px;
    }
    :deep(h1) {
      font-size: 24px;
    }
    :deep(h2) {
      font-size: 20px;
    }
    :deep(h3) {
      font-size: 18px;
    }
    :deep(p), :deep(section), :deep(article) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
      padding: 8px 40px 8px 0px;
    }
    :deep(span) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
    }
    :deep(strong), :deep(b) {
      font-weight: 700;
    }
    :deep(ul), :deep(ol) {
      margin-left: 16px;
      padding: 0;

      li {
        padding-right: 0;
        font-size: 16px;
        line-height: 28px;
        font-weight: 300;
        max-width: 524px;
      }
    }
    :deep(ul) li::marker {
      unicode-bidi: isolate;
      font-variant-numeric: tabular-nums;
      text-transform: none;
      text-indent: 0px !important;
      text-align: start !important;
      text-align-last: start !important;
    }
  }
}
.copy-block {
  background-color: @bg-canvas;
}
</style>

<style lang="less">
@import '@/less/mixins';

.revision-menu-content {
  .copy-menu {
    box-shadow: none !important;
    white-space: nowrap;
    .v-icon {
      font-size: 16px;
    }
  }
}
</style>
